import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { BadgeCheck, Clock, X, Check, FileText, ExternalLink, Download, Users, Briefcase, Scale, AlertCircle } from "lucide-react";
import AdminNav from "@/components/AdminNav";

export default function AdminVerifications() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [v, s] = await Promise.all([
        api.get("/admin/verifications"),
        api.get("/admin/stats"),
      ]);
      setItems(v.data.items);
      setStats(s.data);
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (uid) => { await api.post(`/admin/verifications/${uid}/approve`); load(); };
  const reject = async (uid) => { await api.post(`/admin/verifications/${uid}/reject`); load(); };

  const downloadCert = async (u) => {
    try {
      const { data } = await api.get(`/admin/users/${u.id}/certificate`);
      const f = data.file;
      const bytes = Uint8Array.from(atob(f.data_b64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: f.mime || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = f.filename || `certificado-${u.name}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { alert(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const filtered = items.filter((u) => filter === "all" ? true : (u.verification_status || "pending") === filter);

  return (
    <div className="container-app py-12" data-testid="admin-page">
      <AdminNav />
      <div className="label-eyebrow">Administración</div>
      <h1 className="serif text-5xl mt-3">Verificaciones</h1>
      <p className="mt-2 text-muted-foreground max-w-2xl">
        Revisa la credencial de cada profesional. Para abogados, descarga el certificado de título
        y valídalo en <a href="https://www.pjud.cl" target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] link-underline">pjud.cl</a> antes de aprobar.
      </p>

      {error && <div className="mt-6 panel p-5 text-[hsl(var(--destructive))]">{error}</div>}

      {stats && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats">
          <StatCard icon={<Users size={18}/>} label="Usuarios" value={stats.users} testid="stat-users" />
          <StatCard icon={<Briefcase size={18}/>} label="Abogados" value={stats.lawyers} testid="stat-lawyers" />
          <StatCard icon={<Scale size={18}/>} label="Mediadores" value={stats.mediators} testid="stat-mediators" />
          <StatCard icon={<AlertCircle size={18}/>} label="Pendientes" value={stats.pending_verifications} highlight={stats.pending_verifications > 0} testid="stat-pending" />
          <StatCard icon={<FileText size={18}/>} label="Delegaciones" value={stats.delegations} testid="stat-delegations" />
          <StatCard icon={<Clock size={18}/>} label="Abiertas" value={stats.open_delegations} testid="stat-open" />
          <StatCard icon={<Check size={18}/>} label="Completadas" value={stats.completed_delegations} testid="stat-completed" />
          <StatCard icon={<Users size={18}/>} label="Lista de espera" value={stats.early_access} testid="stat-early" />
        </div>
      )}

      <div className="mt-8 flex gap-2" data-testid="admin-filters">
        {[["pending","Pendientes"],["approved","Aprobados"],["rejected","Rechazados"],["all","Todos"]].map(([v,l]) => (
          <button key={v} onClick={()=>setFilter(v)} className={`pill ${filter === v ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-transparent" : ""}`} data-testid={`admin-filter-${v}`}>{l}</button>
        ))}
      </div>

      <ul className="mt-6 space-y-3" data-testid="admin-list">
        {filtered.length === 0 && <li className="panel p-10 text-center text-muted-foreground">Sin resultados.</li>}
        {filtered.map((u) => {
          const st = u.verification_status || "pending";
          return (
            <li key={u.id} className="panel p-5" data-testid={`admin-row-${u.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="serif text-xl">{u.name}</span>
                    <span className="pill capitalize">{u.role}</span>
                    {st === "approved" && <span className="pill badge-verified"><BadgeCheck size={12}/>Aprobado</span>}
                    {st === "pending" && <span className="pill badge-pending"><Clock size={12}/>Pendiente</span>}
                    {st === "rejected" && <span className="pill" style={{color:"hsl(var(--destructive))"}}>Rechazado</span>}
                    {u.has_certificate && <span className="pill badge-cyan"><FileText size={12}/>Certificado adjunto</span>}
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">{u.email}{u.region ? ` · ${u.comuna || ""}, ${u.region}` : ""}{u.registry_number ? ` · ${u.registry_number}` : ""}{u.rut ? ` · RUT ${u.rut}` : ""}</div>
                  {u.bio && <p className="mt-2 text-sm text-foreground/80 line-clamp-2 max-w-2xl">{u.bio}</p>}

                  {u.role === "lawyer" && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {u.has_certificate ? (
                        <button onClick={()=>downloadCert(u)} className="btn-ghost text-sm py-2" data-testid={`download-cert-${u.id}`}>
                          <Download size={14} className="mr-1.5"/> Descargar certificado
                        </button>
                      ) : (
                        <span className="pill badge-pending">Certificado no subido</span>
                      )}
                      <a href="https://www.pjud.cl" target="_blank" rel="noreferrer" className="btn-ghost text-sm py-2">
                        <ExternalLink size={14} className="mr-1.5"/> Validar en PJUD
                      </a>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  {st !== "approved" && <button onClick={()=>approve(u.id)} className="btn-primary text-sm py-2" data-testid={`approve-${u.id}`}><Check size={14} className="mr-1"/>Aprobar</button>}
                  {st !== "rejected" && <button onClick={()=>reject(u.id)} className="btn-ghost text-sm py-2" data-testid={`reject-${u.id}`}><X size={14} className="mr-1"/>Rechazar</button>}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function StatCard({ icon, label, value, highlight, testid }) {
  return (
    <div
      className={`panel p-4 ${highlight ? "border-[hsl(var(--primary))]/60" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <span className={highlight ? "text-[hsl(var(--primary))]" : ""}>{icon}</span>
        {label}
      </div>
      <div className={`serif text-3xl mt-2 ${highlight ? "text-[hsl(var(--primary))]" : ""}`}>{value ?? 0}</div>
    </div>
  );
}
